/**
 * This file is part of the Haiman.
 * @link     : https://haiman.io/
 * @author   : Emil Zhai (root@derzh.com)
 * @modifier : Emil Zhai (root@derzh.com)
 * @copyright: Copyright (c) 2018 Hangzhou Haila Network Technology Co., Ltd.
 */

import store from '@/store';
import * as api from '@/api/secret';
import { SecretPost } from '@/api/types/secret';
import { SECRET, COMMON } from '@/store/types';
import { StoreDataCache, findCache, saveCache } from '@/store/cache';
import { finalizeAction, StoreActionEnv } from '@/store/actions';
import { updateObject, CreateMultitaskPromise } from '@/utils/util';
import { camelizeString } from '@/utils/transfer';
import { SecretBlocksItem } from '@/components/secret-blocks';
import { parseSecretListBlocks } from '@/components/secret-blocks/utils';

export interface StoreSecretListCacheItem {
  filter: string;
  kw: string;
  sort: string;
  list: SecretBlocksItem[];
  limit: number;
  nextPoint: string | number;
  noMore: boolean;
  updateTime: number;
  pointAttribute: string;
  loading?: boolean;
  pending?: { filter: string; sort: string; kw: string; run: () => Promise<void> };
}

export interface StoreSecretListState {
  contentRevision: number;
  cache: StoreDataCache<StoreSecretListCacheItem>[];
  maxCache: number;
}

const getDefault = (): StoreSecretListCacheItem => ({
  filter: '',
  kw: '',
  sort: '',
  limit: 25,
  list: [],
  nextPoint: '',
  noMore: false,
  updateTime: 0,
  pointAttribute: '',
});

export default {
  namespaced: true,
  state: {
    contentRevision: 0,
    cache: [],
    maxCache: 20,
  },
  getters: {},
  actions: {
    [SECRET.GET_POST_LIST](
      { commit, state }: StoreActionEnv<StoreSecretListState>,
      { action: rawAction, kw = '', sort, filter, limit = 25, route, silent },
    ) {
      const uid = store.state.user.user && store.state.user.user.id;
      const revision = store.state.secret.privateRooms.revision;
      const contentRevision = state.contentRevision;
      if (!filter) {
        throw new Error('List secret filter must be set!');
      }
      const cache = findCache(state.cache, { filter, sort, kw });
      const action = finalizeAction(rawAction, !!cache, cache && cache.noMore);
      if (action && (!cache || !cache.loading)) {
        const nextPoint = cache && action !== 'reload' ? cache.nextPoint : null;
        commit(SECRET.GET_POST_LIST, {
          filter,
          sort,
          kw,
          task: CreateMultitaskPromise(new Promise<void>((resolve, reject) => {
            Promise.all([
              api.getPostList(filter, { kw, point: nextPoint, limit, sort, silent }),
              store.dispatch(`secret/filter/info/${SECRET.GET_FILTER_INFO}`, {
                filter,
                silent,
                async: true,
              }),
              store.dispatch(`secret/${SECRET.GET_EMOTIONS}`, { silent }),
              // store.dispatch(`secret/topic/list/${SECRET.GET_TOPIC_LIST}`),
            ]).then(([res, filterInfo]) => {
              if (uid !== (store.state.user.user && store.state.user.user.id)
                || revision !== store.state.secret.privateRooms.revision || contentRevision !== state.contentRevision) {
                throw new Error('内容权限已变化，请刷新');
              }
              commit(SECRET.GET_POST_LIST_SUCCESS, {
                filter,
                sort,
                kw,
                limit,
                action,
                filterInfo,
                list: parseSecretListBlocks(res.data),
                nextPoint: res.extra && res.extra.nextPoint,
              });
              if (route) {
                store.commit(`common/bus/${COMMON.SAVE_SCROLL}`, {
                  fullPath: route.fullPath,
                  scroll: null,
                });
              }
              resolve();
            }).catch((err) => {
              if (revision === store.state.secret.privateRooms.revision && contentRevision === state.contentRevision
                && uid === (store.state.user.user && store.state.user.user.id)) {
                commit(SECRET.GET_POST_LIST_FAILURE, { filter, sort, kw });
              }
              reject(err);
            });
          })),
        });
      }
      const nc = findCache(state.cache, { filter, sort, kw });
      if (nc && nc.pending) {
        return nc.pending.run();
      }
      return Promise.reject();
    },
  },
  mutations: {
    discardContent(state: StoreSecretListState, { filter }) {
      state.contentRevision += 1;
      state.cache = state.cache.filter(item => item.data.filter !== filter && (!item.data.pending || item.data.pending.filter !== filter));
    },
    [SECRET.POST_UPDATE](state: StoreSecretListState, updateInfo) {
      state.cache.forEach((cache) => {
        const cachePost = cache.data.list.find(p => p.type === 'post' && p.data.id === updateInfo.id);
        if (cachePost && cachePost.type === 'post') {
          updateObject<SecretPost>(cachePost.data, {
            assign: updateInfo.assign,
            offset: updateInfo.offset,
          });
        }
      });
      store.commit(`secret/search/${SECRET.POST_UPDATE}`, updateInfo);
    },
    [SECRET.GET_POST_LIST](state: StoreSecretListState, { filter, sort, kw, task }) {
      const cache = findCache(state.cache, { filter, sort, kw }) || getDefault();
      cache.loading = true;
      cache.pending = { filter, sort, kw, run: task };
      saveCache(state.cache, { filter, sort, kw }, cache, state.maxCache);
    },
    [SECRET.GET_POST_LIST_SUCCESS](state: StoreSecretListState, { action, filter, sort, kw, limit, filterInfo, list, nextPoint }) {
      const cache = findCache(state.cache, { filter, sort, kw }) || getDefault();
      if (action === 'reload') {
        const sortInfo = filterInfo.orders ? filterInfo.orders.find(p => p.value === sort) : null;
        cache.sort = sort;
        cache.noMore = false;
        const pointAttribute = sortInfo ? sortInfo.pointAttribute : filterInfo.pointAttribute;
        cache.pointAttribute = pointAttribute ? camelizeString(pointAttribute) : '';
      }
      cache.filter = filter;
      cache.kw = kw;
      cache.limit = limit;
      cache.list = action === 'reload' ? list : cache.list.concat(list);
      cache.updateTime = new Date().valueOf();
      cache.noMore = !nextPoint;
      cache.nextPoint = nextPoint;
      cache.loading = false;
      saveCache(state.cache, { filter, sort, kw }, cache, state.maxCache);
    },
    [SECRET.GET_POST_LIST_FAILURE](state: StoreSecretListState, { filter, sort, kw }) {
      const cache = findCache(state.cache, { filter, sort, kw });
      if (cache) {
        cache.loading = false;
        saveCache(state.cache, { filter, sort, kw }, cache, state.maxCache);
      }
    },
  },
};
